import React, {createContext, useContext, useEffect, useState} from 'react';
import DataController from '../classes/DataController';
import BooksController from '../classes/BooksController';
import RemoteDataController from '../classes/RemoteDataController';

// Define initial context shape
const BooksContext = createContext({});

const BooksProvider = ({
  user,
  children,
  isDownloadingOnlineData,
  onBooksLoaded,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const getLocalBooks = async () => {
    try {
      const currentBooksIndex = await DataController.getBooksIndexFile();
      const booksForUser = BooksController.getBooksForUser(
        currentBooksIndex,
        user,
      );
      const processedBooks = await BooksController.processBooks(booksForUser);
      return processedBooks;
    } catch (err) {
      console.error('Error fetching local books:', err);
      return [];
    }
  };

  const getRemoteBooks = async () => {
    setIsLoading(true);
    try {
      const localBooksIndex = await DataController.getBooksIndexFile();
      const remoteBooks = await RemoteDataController.downloadBooksIndex();
      const booksForUser = BooksController.getBooksForUser(remoteBooks, user);
      const booksToUpdate = await BooksController.findBooksToUpdate(
        localBooksIndex,
        booksForUser,
      );

      if (!booksToUpdate?.length) {
        console.log('No books to process, skipping');
        return;
      }

      await RemoteDataController.asyncPool(
        3,
        booksToUpdate,
        BooksController.updateBook,
      );
    } catch (err) {
      console.error('Error fetching remote books:', err);
    }
  };

  useEffect(() => {
    if (!user?.hasBookAccess) {
      console.log('User does not have book access, skipping book download');
      return;
    }
    getRemoteBooks()
      .then(() => getLocalBooks())
      .then(books => onBooksLoaded(books))
      .catch(err => console.error('Error fetching books:', err))
      .finally(() => setIsLoading(false));
  }, [user?.hasBookAccess, isDownloadingOnlineData]);

  return (
    <BooksContext.Provider value={{isLoading}}>
      {children}
    </BooksContext.Provider>
  );
};

const useBooks = () => {
  const context = useContext(BooksContext);
  if (!context) {
    throw new Error('useBooks must be used within a BooksProvider');
  }
  return context;
};

export {BooksProvider, useBooks};
